<template>
  <div v-if="loading" class="text-center py-16">
    <v-progress-circular indeterminate color="#f59e0b" size="56" width="4" />
    <div class="text-body-2 text-medium-emphasis mt-4">Loading series volumes...</div>
  </div>

  <v-alert v-else-if="error" type="error" class="my-6" variant="tonal">{{ error }}</v-alert>

  <div v-else-if="volumes.length" class="serie-detail-view">
    <!-- Header Navigation -->
    <div class="d-flex align-center justify-space-between mb-6">
      <v-btn
        to="/comics/homeview"
        variant="tonal"
        size="small"
        class="btn-modern"
        prepend-icon="mdi-arrow-left"
      >
        Back to Comics
      </v-btn>
    </div>

    <!-- Series Overview Banner -->
    <v-card class="glass-card pa-6 pa-md-8 mb-8">
      <div class="d-flex flex-wrap align-center justify-space-between ga-4">
        <div>
          <div class="d-flex align-center ga-2 mb-2">
            <v-chip color="#f59e0b" variant="flat" size="small" class="text-white font-weight-bold">
              COMIC SERIES
            </v-chip>
            <v-chip v-if="serieCategory" size="small" variant="tonal">
              {{ serieCategory }}
            </v-chip>
          </div>
          <h1 class="text-h3 font-weight-bold mb-2">{{ serieName }}</h1>
          <p v-if="serieAuthor" class="text-subtitle-1 text-medium-emphasis mb-0">
            By <strong class="text-high-emphasis">{{ serieAuthor }}</strong>
          </p>
        </div>

        <div class="series-stats-badge pa-4 rounded-xl text-center">
          <div class="text-h4 font-weight-bold text-warning">{{ volumes.length }}</div>
          <div class="text-caption text-medium-emphasis font-weight-bold">Volumes in Library</div>
        </div>
      </div>
    </v-card>

    <!-- Volumes Grid -->
    <div class="poster-dense-grid">
      <div
        v-for="comic in volumes"
        :key="comic.id"
        class="grid-poster-item"
      >
        <router-link
          :to="{ name: 'ComicDetail', params: { id: comic.id } }"
          class="poster-card poster-card--comics d-block text-decoration-none"
        >
          <div class="grid-poster-container">
            <v-img
              :src="posterSrc(comic)"
              :aspect-ratio="2 / 3"
              cover
              class="rounded-lg w-100"
            >
              <template #placeholder>
                <div class="d-flex flex-column align-center justify-center fill-height fallback-poster">
                  <v-icon size="36" color="#f59e0b">mdi-draw</v-icon>
                  <span class="text-caption text-center px-2 mt-1 text-truncate w-100 font-weight-medium">
                    {{ comic.title }}
                  </span>
                </div>
              </template>
            </v-img>

            <div class="poster-badges-top">
              <div v-if="comic.tome" class="issue-badge">
                Tome {{ comic.tome }}
              </div>
              <div v-else-if="comic.issue_number" class="issue-badge">
                #{{ comic.issue_number }}
              </div>
            </div>

            <div class="poster-overlay-bottom">
              <div class="poster-title text-truncate font-weight-bold">{{ comic.title }}</div>
              <div class="poster-subtitle d-flex justify-space-between align-center">
                <span v-if="comic.tome" class="text-truncate">Tome {{ comic.tome }}</span>
                <span v-else-if="comic.year" class="ml-auto">{{ comic.year }}</span>
              </div>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import ComicsAPI from '@/services/comics'
import { posterSrc } from '@/utils/comicForm'

export default {
  name: 'ComicSerieDetail',
  setup() {
    const route = useRoute()
    const volumes = ref([])
    const loading = ref(true)
    const error = ref('')

    const serieName = computed(() => volumes.value[0]?.serie || 'Series')
    const serieAuthor = computed(() => volumes.value[0]?.author || '')
    const serieCategory = computed(() => volumes.value[0]?.category || '')

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const { data } = await ComicsAPI.getBySerieId(route.params.serieId)
        volumes.value = data
      } catch (err) {
        error.value = err.response?.data?.detail?.message || 'Series not found'
      } finally {
        loading.value = false
      }
    }

    onMounted(load)

    return {
      volumes,
      loading,
      error,
      serieName,
      serieAuthor,
      serieCategory,
      posterSrc,
    }
  },
}
</script>

<style scoped>
.serie-detail-view {
  max-width: 1240px;
  margin: 0 auto;
}

.series-stats-badge {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.25);
  min-width: 130px;
}

.poster-dense-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .poster-dense-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
}

.grid-poster-container {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  background: rgba(128, 128, 128, 0.08);
}

.fallback-poster {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1));
}

.poster-badges-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.issue-badge {
  background: rgba(245, 158, 11, 0.9);
  backdrop-filter: blur(8px);
  color: #000;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: auto;
}

.poster-overlay-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px 8px 8px 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
  color: #fff;
  z-index: 2;
}

.poster-title {
  font-size: 0.8rem;
  line-height: 1.2;
}

.poster-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}
</style>

